export const ACTIVITY_DATA_ITEM_KEY = "activityId";
export const ACTIVITY_SELECTED_FIELD = "selected";

export function getActivitiesToDelete(dataItem, selectedState, listData = []) {
    const selectedIds = Object.keys(selectedState).filter(
        (key) => selectedState[key],
    );
    if (selectedIds.length > 1 && selectedState[dataItem.activityId]) {
        return listData.filter((item) => selectedState[item.activityId]);
    }
    return [dataItem];
}

export function getDeleteConfirmationMessage(items) {
    const itemsArray = Array.isArray(items) ? items : [items];
    if (itemsArray.length > 1) {
        return `Are you sure you want to delete ${itemsArray.length} selected activities?`;
    }
    return `Are you sure you want to delete activity '${itemsArray[0]?.details}'?`;
}

export function getDeleteSuccessMessage(items) {
    const itemsArray = Array.isArray(items) ? items : [items];
    return itemsArray.length > 1
        ? `${itemsArray.length} activities deleted successfully`
        : "Activity deleted successfully";
}

export function resolveDuplicateActivityId(sourceActivityOrId) {
    return typeof sourceActivityOrId === "object"
        ? sourceActivityOrId?.activityId
        : sourceActivityOrId;
}

export const getActivityTagId = (tag) =>
    tag?.tagId ?? tag?.activityTagID ?? tag?.id;

export function mergeDuplicateActivityTags(apiTags, sourceTags) {
    if (!Array.isArray(apiTags)) return sourceTags ?? apiTags;
    if (!sourceTags?.length) return apiTags;

    const sourceById = new Map(
        sourceTags
            .filter((tag) => getActivityTagId(tag) != null)
            .map((tag) => [String(getActivityTagId(tag)), tag]),
    );

    return apiTags.map((tag) => {
        const source = sourceById.get(String(getActivityTagId(tag)));
        return source ? { ...source, ...tag } : tag;
    });
}

export function mergeDuplicateActivityData(apiData, sourceRow) {
    if (!sourceRow) return apiData || {};
    return {
        ...(apiData || {}),
        documentId: apiData?.documentId ?? sourceRow.documentId,
        documentName: apiData?.documentName ?? sourceRow.documentName,
        documentFile: apiData?.documentFile ?? sourceRow.documentFile,
        fileId: apiData?.fileId ?? sourceRow.fileId,
        tags: mergeDuplicateActivityTags(apiData?.tags, sourceRow.tags),
    };
}

export function truncateFileName(fileName) {
    if (!fileName) return;
    const extensionIndex = fileName.lastIndexOf(".");

    if (extensionIndex === -1) {
        return fileName.length > 8 ? fileName.substring(0, 18) : fileName;
    }

    const extension = fileName.substring(extensionIndex);

    if (fileName.length > 8) {
        return fileName.substring(0, 7 - extension.length) + extension;
    }

    return fileName;
}

export function getStatusClass(status) {
    switch (status) {
        case "Posted":
            return "bg-success";
        case "Rejected":
            return "bg-danger";
        default:
            return "bg-secondary";
    }
}

function getTagDisplayColour(tag) {
    const colour = tag?.colour?.trim();
    if (!colour || colour.toLowerCase() === "none") {
        return null;
    }
    return colour;
}

function getContrastColor(hexColor) {
    if (!hexColor || typeof hexColor !== "string") {
        return "#000";
    }

    const hex = hexColor.replace("#", "");

    if (!/^#?[0-9A-Fa-f]{6}$/.test(hex)) {
        return "#000";
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    return brightness > 128 ? "#000000" : "#FFFFFF";
}

export function getActivityTagChipStyle(tag) {
    const colour = getTagDisplayColour(tag);

    return {
        backgroundColor: colour ?? "#F8F9FA",
        color: colour ? getContrastColor(colour) : "#495057",
        padding: "2px 8px",
        borderRadius: "999px",
        fontSize: "11px",
        lineHeight: "14px",
        fontWeight: 500,
    };
}

export function getActivitySubtypeStatus(activity) {
    if (
        activity?.subType === "ChitRequest" ||
        activity?.subType === "BillRequest"
    ) {
        return activity.subTypeStatus || "";
    }
    return "";
}

export function getRowStyle(dataItem, activityTypes = []) {
    const activityType = activityTypes.find(
        (type) => type.activityTypeID === dataItem.typeId,
    );

    if (
        activityType &&
        activityType.rowStyle &&
        typeof activityType.rowStyle === "string"
    ) {
        const match = activityType.rowStyle.match(/backgroundColor:['"]([^'"]+)['"]/);
        if (match?.[1]) {
            return { backgroundColor: match[1] };
        }
    }

    return {};
}

export function getSelectedDocumentIds(selectedState, listData = []) {
    return Object.keys(selectedState)
        .filter((key) => selectedState[key])
        .map((key) => {
            const selectedItem = listData.find((item) => item.activityId == key);
            return selectedItem?.documentId;
        })
        .filter((id) => id);
}

export function getSelectedDocumentsForShare(selectedState, listData = []) {
    return Object.keys(selectedState)
        .filter((key) => selectedState[key])
        .map((key) => {
            const selectedItem = listData.find((item) => item.activityId == key);
            return {
                id: selectedItem?.documentId,
                name: selectedItem?.documentName,
            };
        })
        .filter((item) => item.id && item.name);
}

export function buildMappedGridData(
    list,
    selectedState,
    idGetter,
    selectedField = ACTIVITY_SELECTED_FIELD,
) {
    return {
        data: list?.data?.map((item) => ({
            ...item,
            [selectedField]: selectedState[idGetter(item)],
        })),
        total: list?.total,
    };
}

export function cleanEmailSubjectForDetails(subject = "") {
    if (!subject) return "";
    return String(subject).replace(/\s+/g, " ").trim();
}

export function cleanFileNameForDetails(fileName = "") {
    if (!fileName) return "";
    const extensionIndex = fileName.lastIndexOf(".");
    const base = extensionIndex > 0 ? fileName.substring(0, extensionIndex) : fileName;

    return base
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

export const ACTIVITY_SYSTEM_TYPE = {
    EMAIL_IN: "EmailIn",
    EMAIL_OUT: "EmailOut",
    DOCUMENT_OUT: "DocumentOut",
};

export const DOCUMENT_FILE_EXTENSIONS = ["doc", "docx", "pdf", "xls", "xlsx"];

export function getFileExtension(fileName = "") {
    const ext = String(fileName ?? "").split(".").pop();
    return ext ? ext.toLowerCase() : "";
}

export function isEmailFile(fileName = "") {
    const ext = getFileExtension(fileName);
    return ext === "eml" || ext === "msg";
}

export function getEmailFileExt(fileName = "") {
    return getFileExtension(fileName);
}

export function findActivityTypeBySystemType(
    activityTypes,
    systemType,
    fallbackCode,
) {
    if (!Array.isArray(activityTypes)) return null;
    return (
        activityTypes.find((item) => item.systemType === systemType) ||
        activityTypes.find((item) => item.code === fallbackCode) ||
        null
    );
}

export function extractEmailDomain(emailValue) {
    if (!emailValue) return null;
    const m = String(emailValue).match(/<?([^\s<>]+@[^\s<>]+)>?/);
    if (!m) return null;
    const addr = m[1];
    const at = addr.lastIndexOf("@");
    if (at === -1) return null;
    return addr.slice(at + 1).trim().toLowerCase();
}

export function domainsMatch(a, b) {
    if (!a || !b) return false;
    const aa = String(a).trim().toLowerCase();
    const bb = String(b).trim().toLowerCase();
    if (aa === bb) return true;
    return aa.endsWith("." + bb) || bb.endsWith("." + aa);
}

export function resolveEmailActivityType(activityTypes, fromValue, companyDomains) {
    if (!Array.isArray(activityTypes) || activityTypes.length === 0) return null;
    const fromValueStr = (fromValue ?? "").toString().trim();
    const domainList = (Array.isArray(companyDomains)
        ? companyDomains
        : String(companyDomains ?? "").split(",")
    )
        .map((d) => d.trim())
        .filter(Boolean);

    let outgoing;
    if (!fromValueStr || !fromValueStr.includes("@")) {
        outgoing = true;
    } else {
        const fromDomain = extractEmailDomain(fromValueStr);
        outgoing = domainList.some((d) => domainsMatch(fromDomain, d));
    }

    const emailOut = findActivityTypeBySystemType(
        activityTypes,
        ACTIVITY_SYSTEM_TYPE.EMAIL_OUT,
        "EO",
    );
    if (outgoing) return emailOut;

    return (
        findActivityTypeBySystemType(
            activityTypes,
            ACTIVITY_SYSTEM_TYPE.EMAIL_IN,
            "EI",
        ) || emailOut
    );
}

export function resolveActivityTypeForFile(
    activityTypes,
    { filename, fromAddress } = {},
    companyDomains,
) {
    if (!Array.isArray(activityTypes) || activityTypes.length === 0) return null;

    if (isEmailFile(filename)) {
        return resolveEmailActivityType(activityTypes, fromAddress, companyDomains);
    }

    if (DOCUMENT_FILE_EXTENSIONS.includes(getFileExtension(filename))) {
        return findActivityTypeBySystemType(
            activityTypes,
            ACTIVITY_SYSTEM_TYPE.DOCUMENT_OUT,
            "DO",
        );
    }

    return null;
}

export function parseDownloadFilename(response, asPDF = false) {
    let fileName;
    const contentDisposition = response.headers["content-disposition"];

    if (response.data?.filename) {
        fileName = response.data.filename;
    } else if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (fileNameMatch?.[1]) {
            fileName = fileNameMatch[1].replace(/['"]/g, "");
        }
    }
    if (!fileName) {
        fileName =
            (response.data?.fileID || "downloaded-document") + (asPDF ? ".pdf" : "");
    }
    return fileName;
}

export function downloadBlobAsFile(data, fileName) {
    const blob = new Blob([data]);
    const url = window.URL.createObjectURL(blob);
    const tempLink = document.createElement("a");
    tempLink.href = url;
    tempLink.setAttribute("download", fileName);
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
    window.URL.revokeObjectURL(url);
}

export function applyActivitiesDragTransfer(
    event,
    dataItem,
    { listData = [], selectedState, idGetter, defaultMatterId },
) {
    const selectedItems = listData.filter((item) => selectedState[idGetter(item)]);

    const itemsToDrag =
        selectedItems.length > 0 && selectedState[idGetter(dataItem)]
            ? selectedItems
            : [dataItem];

    const selectedDocs = itemsToDrag
        .filter((item) => item.documentId)
        .map((item) => ({
            documentId: item.documentId,
            documentName: item.documentName || item.details,
            matterId: item.matterId || defaultMatterId,
            activityId: item.activityId,
            details: item.details,
        }));

    if (selectedDocs.length === 0) {
        event.preventDefault();
        return;
    }

    const payload = {
        documents: selectedDocs.map((d) => ({
            documentId: d.documentId,
            documentName: d.documentName,
            matterId: d.matterId,
            activityId: d.activityId,
        })),
    };

    event.dataTransfer.setData(
        "text/plain",
        "LINQ_CASE_ACTIVITIES:" + JSON.stringify(payload),
    );
    event.dataTransfer.effectAllowed = "copy";
}
